/**
 * Refund resource — list, detail, create, edit with lifecycle actions.
 * Refunds return funds against receipts; Approved refunds post a reversing
 * transaction and flip the receipt to Refunded.
 *
 * @see Documentation/Specification/020_Definitions/application-agnostic/ledger/Refund.md
 * @see Documentation/Specification/04_Processes/application-agnostic/ledger/Receipting.md
 */
import { useState } from 'react';
import {
  List, Show, Create, Edit, Datagrid, TextField, DateField, NumberField, ChipField,
  SimpleForm, TextInput, SelectInput, NumberInput, DateInput, Filter, SearchInput,
  useListContext, useRecordContext, useNotify, useRefresh, required, maxValue,
} from 'react-admin';
import { useWatch } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import {
  Box, Typography, Paper, Grid, Chip, Alert, Stepper, Step, StepLabel,
  Button, Stack,
} from '@mui/material';
import { Receipt, CheckCircle, Cancel, MoneyOff, Info } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ListPageHeader } from '@/components/ListPageHeader';
import { PageDescription } from '@/components/help';
import { useEntitlement } from '@/components/domain-engine/useEntitlement';
import { DEFAULT_TENANT_ID } from '@/components/domain-engine/TenantSelector';
import { fetchApi } from '@/api/fetchApi';
import type { ChipColor } from '@/types/chip';
import type { RefundResponse } from '@rosta/data-model/application-agnostic/ledger/dtos';

type RefundStatusValue = 'Pending' | 'Approved' | 'Posted' | 'Rejected' | 'Cancelled';

const REFUND_STATUSES = [
  { id: 'Pending', name: 'Pending' },
  { id: 'Approved', name: 'Approved' },
  { id: 'Posted', name: 'Posted' },
  { id: 'Rejected', name: 'Rejected' },
  { id: 'Cancelled', name: 'Cancelled' },
];

const REASON_CODES = [
  { id: 'Duplicate', name: 'Duplicate' },
  { id: 'ServiceIssue', name: 'Service Issue' },
  { id: 'ClientCredit', name: 'Client Credit' },
  { id: 'ContractCancellation', name: 'Contract Cancellation' },
  { id: 'Other', name: 'Other' },
];

const STATUS_COLORS: Record<RefundStatusValue, ChipColor> = {
  Pending: 'warning',
  Approved: 'info',
  Posted: 'success',
  Rejected: 'error',
  Cancelled: 'default',
};

const STATUS_BANNER: Record<RefundStatusValue, { severity: 'success' | 'info' | 'warning' | 'error'; msg: string }> = {
  Pending: { severity: 'warning', msg: 'Refund awaiting approval' },
  Approved: { severity: 'info', msg: 'Refund approved — ready to post' },
  Posted: { severity: 'success', msg: 'Refund posted — receipt refunded' },
  Rejected: { severity: 'error', msg: 'Refund rejected' },
  Cancelled: { severity: 'info', msg: 'Refund cancelled' },
};

const formatCurrency = (amount: number | undefined, currency: string = 'ZAR'): string =>
  amount === undefined ? '—' : `${currency} ${amount.toLocaleString()}`;

export const RefundFilters = () => (
  <Filter>
    <SearchInput source="q" placeholder="Search refund number" alwaysOn />
    <SelectInput source="status" choices={REFUND_STATUSES} />
    <SelectInput source="reasonCode" choices={REASON_CODES} />
    <DateInput source="fromDate" label="From date" />
    <DateInput source="toDate" label="To date" />
  </Filter>
);

export const RefundList = () => (
  <List filters={<RefundFilters />}>
    <RefundListHeader />
    <Datagrid rowClick="show">
      <TextField source="refundNumber" />
      <TextField source="receiptRef.id" label="Receipt" />
      <NumberField source="amount" options={{ style: 'currency', currency: 'ZAR' }} />
      <TextField source="reasonCode" />
      <ChipField source="status" />
      <DateField source="refundDate" />
    </Datagrid>
  </List>
);

const RefundListHeader = () => {
  const { data: refundsRaw } = useListContext();
  const typed = refundsRaw as unknown as RefundResponse[] | undefined;
  const total = typed?.length ?? 0;
  const pending = typed?.filter((r) => r.status === 'Pending').length ?? 0;
  const approved = typed?.filter((r) => r.status === 'Approved').length ?? 0;
  const posted = typed?.filter((r) => r.status === 'Posted').length ?? 0;
  const totalValue = typed?.reduce((sum, r) => sum + (r.amount ?? 0), 0) ?? 0;

  return (
    <ListPageHeader
      title="Refunds"
      metrics={[
        { label: 'Total Refunds', value: String(total), color: 'primary.main' },
        { label: 'Refund Value', value: `R ${(totalValue / 1000000).toFixed(1)}M`, color: 'info.main' },
        { label: 'Pending', value: String(pending), color: 'warning.main' },
        { label: 'Posted', value: String(posted), color: 'success.main' },
      ]}
      pieChartData={[
        { label: 'Pending', value: pending },
        { label: 'Approved', value: approved },
        { label: 'Posted', value: posted },
      ].filter((d) => d.value > 0)}
      pieChartTitle="Refund Status"
    />
  );
};

/**
 * Transforms flat react-admin form values (`receiptRef.id`) into the nested
 * ref shape (`{ type, id }`) the API's CreateRefundDto requires.
 */
export const refundTransform = (data: Record<string, unknown>): Record<string, unknown> => {
  const out = { ...data };
  // Normalize any flat dotted keys (e.g. `receiptRef.id`) into nested shape first.
  for (const key of Object.keys(out)) {
    if (!key.includes('.')) continue;
    const [parent, child] = key.split('.');
    const val = out[key];
    const parentVal = out[parent] as Record<string, unknown> | undefined;
    if (val !== undefined && val !== null && val !== '' && !parentVal?.id) {
      out[parent] = { ...(parentVal ?? {}), [child]: val };
    }
    delete out[key];
  }
  for (const [field, type] of [
    ['receiptRef', 'receipt'],
    ['invoiceRef', 'invoice'],
    ['revenueAccountRef', 'account'],
    ['bankAccountRef', 'account'],
  ] as const) {
    const value = out[field] as { id?: string } | undefined;
    if (value?.id) {
      out[field] = { type, id: value.id };
    } else {
      delete out[field];
    }
  }
  if (!out.reasonNote) delete out.reasonNote;
  if (!out.refundNumber) delete out.refundNumber;
  if (!out.refundDate) delete out.refundDate;
  if (!out.currency) delete out.currency;
  return out;
};

const RefundShowContent = () => {
  const record = useRecordContext<RefundResponse>();
  const notify = useNotify();
  const refresh = useRefresh();
  const navigate = useNavigate();
  const { hasEntitlement } = useEntitlement(DEFAULT_TENANT_ID);
  const [busy, setBusy] = useState(false);
  if (!record) return null;

  const status = (record.status ?? 'Pending') as RefundStatusValue;
  const banner = STATUS_BANNER[status];
  const canApprove = status === 'Pending';
  const canPost = status === 'Approved';
  const activeStep = status === 'Posted' ? 2 : status === 'Approved' ? 1 : 0;
  const ledgerEntitled = hasEntitlement('ledger');

  const runAction = async (action: 'approve' | 'reject' | 'cancel' | 'post') => {
    let rejectReason: string | null = null;
    if (action === 'reject') {
      rejectReason = window.prompt('Rejection reason:');
      if (!rejectReason) return;
    }
    if (action === 'post' && !window.confirm('Post this refund? This creates a reversing transaction.')) return;
    setBusy(true);
    try {
      const recordId = (record as RefundResponse & { id?: string }).id ?? record._id;
      await fetchApi(`/refunds/${recordId}/${action}`, {
        method: 'POST',
        body: action === 'reject' ? JSON.stringify({ reason: rejectReason }) : undefined,
      });
      notify(`Refund ${action}d`, { type: 'success' });
      refresh();
    } catch {
      notify(`Refund ${action} failed`, { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <PageDescription
        entity="Refund"
        description="A refund returning funds against a receipt. Approved refunds post a reversing transaction and mark the receipt refunded."
        icon={<Receipt />}
      />

      {banner && (
        <Alert
          severity={banner.severity}
          sx={{ mb: 2 }}
          icon={status === 'Posted' ? <CheckCircle /> : status === 'Rejected' ? <Cancel /> : <Info />}
        >
          {banner.msg}
        </Alert>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <MoneyOff color="primary" fontSize="large" />
        <Box>
          <Typography variant="h4" fontWeight={700}>{record.refundNumber}</Typography>
          <Typography variant="caption" color="text.secondary">
            Receipt {record.receiptRef?.id} · {record.refundDate}
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Amount</Typography>
            <Typography variant="h6" fontWeight={700} color="error.main" sx={{ mt: 0.5 }}>
              {formatCurrency(record.amount, record.currency)}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Status</Typography>
            <Box sx={{ mt: 0.5 }}>
              <Chip label={record.status} size="small" color={STATUS_COLORS[status]} />
            </Box>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Reason Code</Typography>
            <Typography variant="body1" sx={{ mt: 0.5 }}>{record.reasonCode}</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Invoice</Typography>
            <Typography variant="body1" sx={{ mt: 0.5 }}>{record.invoiceRef?.id ?? '—'}</Typography>
          </Paper>
        </Grid>
        {record.reasonNote && (
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Reason Note</Typography>
              <Typography variant="body1" sx={{ mt: 0.5 }}>{record.reasonNote}</Typography>
            </Paper>
          </Grid>
        )}
      </Grid>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>Refund Lifecycle</Typography>
        <Stepper activeStep={activeStep} alternativeLabel>
          <Step><StepLabel>Pending</StepLabel></Step>
          <Step><StepLabel>Approved</StepLabel></Step>
          <Step><StepLabel>Posted</StepLabel></Step>
        </Stepper>
      </Paper>

      {ledgerEntitled && (canApprove || canPost) && (
        <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
          {canApprove && (
            <>
              <Button variant="contained" color="success" disabled={busy} onClick={() => runAction('approve')}>
                Approve
              </Button>
              <Button variant="outlined" color="error" disabled={busy} onClick={() => runAction('reject')}>
                Reject
              </Button>
            </>
          )}
          {canPost && (
            <Button variant="contained" color="primary" disabled={busy} onClick={() => runAction('post')}>
              Post Refund
            </Button>
          )}
        </Stack>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button onClick={() => navigate('/refunds')}>Back to Refunds</Button>
      </Box>
    </Box>
  );
};

export const RefundShow = () => (
  <Show>
    <RefundShowContent />
  </Show>
);

/** Live refundable-outstanding lookup for the selected receipt (server re-validates on post). */
function useRefundableOutstanding() {
  const receiptId = useWatch({ name: 'receiptRef.id' }) as string | undefined;
  return useQuery({
    queryKey: ['refundable-outstanding', receiptId],
    queryFn: () =>
      fetchApi(
        `/refunds/receipt/${encodeURIComponent(receiptId as string)}/refundable-outstanding`,
      ) as Promise<{ receiptId: string; receiptAmount: number; refundableOutstanding: number }>,
    enabled: Boolean(receiptId),
    retry: false,
  });
}

const RefundableOutstandingHint = () => {
  const receiptId = useWatch({ name: 'receiptRef.id' }) as string | undefined;
  const { data, isFetching, isError } = useRefundableOutstanding();
  if (!receiptId) return null;
  return (
    <Alert severity={isError ? 'warning' : 'info'} sx={{ mb: 1 }}>
      {isFetching
        ? 'Checking refundable outstanding…'
        : data
          ? `Refundable outstanding: ${formatCurrency(data.refundableOutstanding)} (receipt total ${formatCurrency(data.receiptAmount)})`
          : 'Refundable outstanding unavailable for this receipt'}
    </Alert>
  );
};

const RefundAmountInput = () => {
  const { data } = useRefundableOutstanding();
  const max = data?.refundableOutstanding;
  const validate =
    max !== undefined
      ? [required(), maxValue({ max, message: `Amount exceeds refundable outstanding (${formatCurrency(max)})` })]
      : required();
  return <NumberInput source="amount" validate={validate} fullWidth helperText={max !== undefined ? `Maximum ${formatCurrency(max)}` : undefined} />;
};

export const RefundCreate = () => (
  <Create transform={refundTransform}>
    <SimpleForm defaultValues={{ currency: 'ZAR', refundDate: new Date().toISOString().slice(0, 10) }}>
      <TextInput source="receiptRef.id" label="Receipt ID" validate={required()} fullWidth helperText="ID of the receipt being refunded" />
      <RefundableOutstandingHint />
      <TextInput source="invoiceRef.id" label="Invoice ID" fullWidth helperText="Optional — linked invoice" />
      <RefundAmountInput />
      <SelectInput source="currency" choices={[{ id: 'ZAR', name: 'ZAR' }, { id: 'USD', name: 'USD' }]} defaultValue="ZAR" />
      <SelectInput source="reasonCode" choices={REASON_CODES} validate={required()} fullWidth />
      <TextInput source="reasonNote" label="Reason Note" fullWidth multiline helperText="Required when reason code is Other" />
      <DateInput source="refundDate" label="Refund Date" />
      <TextInput source="revenueAccountRef.id" label="Revenue Account ID" fullWidth helperText="Optional — defaults to first active income account" />
      <TextInput source="bankAccountRef.id" label="Bank Account ID" fullWidth helperText="Optional — defaults to first active asset account" />
    </SimpleForm>
  </Create>
);

export const RefundEdit = () => (
  <Edit>
    <SimpleForm>
      <TextInput source="reasonNote" label="Reason Note" fullWidth multiline />
      <DateInput source="refundDate" label="Refund Date" />
    </SimpleForm>
  </Edit>
);
